/**
 * =========================================================
 * service-hub/hooks.ts — TanStack Query hooks
 * =========================================================
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getMySubscriptions,
  getServiceCatalog,
  paySubscription,
  requestActivation,
  type ActivationPayload,
} from "./api";
import { useAuthStore } from "@/lib/auth/authStore";

export const serviceHubKeys = {
  catalog:       ["service-hub", "catalog"] as const,
  subscriptions: (businessId?: number | string | null) => ["service-hub", "subscriptions", businessId ?? "default"] as const,
};

const useActiveOrganizationId = () =>
  useAuthStore((state) => state.activeOrganizationContext?.organization_id ?? null);

/** Katalogu publik i shërbimeve (5 min cache). */
export const useServiceCatalog = () =>
  useQuery({
    queryKey: serviceHubKeys.catalog,
    queryFn:  getServiceCatalog,
    staleTime: 5 * 60 * 1_000,
  });

/** Abonimet aktuale të biznesit. */
export const useMySubscriptions = (businessId?: number | null) => {
  const organizationId = useActiveOrganizationId();
  const resolvedBusinessId = businessId ?? organizationId;
  return useQuery({
    queryKey: serviceHubKeys.subscriptions(resolvedBusinessId),
    queryFn:  () => getMySubscriptions(resolvedBusinessId ? String(resolvedBusinessId) : null),
    enabled: Boolean(resolvedBusinessId),
    staleTime: 30_000,
  });
};

/** Kërko aktivizim të një shërbimi. */
export const useRequestActivation = (businessId?: number | null, onSuccess?: (msg: string) => void) => {
  const qc = useQueryClient();
  const organizationId = useActiveOrganizationId();
  const resolvedBusinessId = businessId ?? organizationId;
  return useMutation({
    mutationFn: (payload: ActivationPayload) => requestActivation(payload, resolvedBusinessId ? String(resolvedBusinessId) : null),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ["service-hub", "subscriptions"] });
      void qc.invalidateQueries({ queryKey: ["capabilities"] });
      onSuccess?.(data.message);
    },
  });
};

/** Paguaj abonimin pasi kerkesa eshte krijuar. */
export const usePaySubscription = (businessId?: number | null, onSuccess?: (msg: string) => void) => {
  const qc = useQueryClient();
  const organizationId = useActiveOrganizationId();
  const resolvedBusinessId = businessId ?? organizationId;
  return useMutation({
    mutationFn: (subscriptionId: number) => paySubscription(subscriptionId, resolvedBusinessId ? String(resolvedBusinessId) : null),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ["service-hub", "subscriptions"] });
      void qc.invalidateQueries({ queryKey: ["capabilities"] });
      onSuccess?.(data.message);
    },
  });
};

// =========================================================
// Fund i service-hub/hooks.ts
// =========================================================
